<script>
import { format, subDays, addDays, differenceInCalendarDays } from "date-fns";
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import Spinner from "@/components/ui/Spinner";
import ordersCrmService from "@/services/orders-crm";
import helperService from "@/services/helper";
import {
  getMedicalInsuranceName,
  getMedicalInsurancePlan,
  getMedicalInsuranceNumber,
} from "@/services/patient-utils";
import {
  STATUS_META,
  CURRENT_STATUSES,
  ASSIGNABLE_STATUSES,
  TERMINAL_STATUSES,
  statusLabel,
} from "@/services/orders-crm-statuses";
import OrdersCrmStats from "./OrdersCrmStats.vue";

const MAX_RANGE_DAYS = 15;

const SHOW_EXPORT_CSV_BUTTON = true;

const TYPE_META = {
  lab: { icon: "labs", label: "Lab" },
  images: { icon: "assignment", label: "Imagen" },
  practices: { icon: "assignment", label: "Práctica" },
};

// Persisted at module scope (not in component data) so the combo options
// aren't re-fetched every time an assistant navigates back into the module
// within the same session.
const filtersCache = { providers: null, coverages: null, expiresAt: 0 };
const FILTERS_CACHE_TTL_MS = 5 * 60 * 1000;

export default {
  name: "OrdersCRM",

  components: {
    BaseSubNav,
    Spinner,
    OrdersCrmStats,
  },

  data() {
    return {
      view: "inbox",
      showExportCsvButton: SHOW_EXPORT_CSV_BUTTON,
      docs: [],
      total: 0,
      loading: false,
      exporting: false,
      loadDebounce: null,
      loadSeq: 0,
      providers: [],
      coverages: [],
      counts: { active: 0, takenThisWeek: 0, closed: 0 },
      page: 1,
      pageSize: 25,
      filters: {
        search: "",
        from: format(subDays(new Date(), 7), "yyyy-MM-dd"),
        to: format(new Date(), "yyyy-MM-dd"),
        type: "",
        status: "all",
        doctor: "",
        coverages: [],
      },
      detail: {
        visible: false,
        order: null,
        observations: "",
        savingObservations: false,
      },
      statusChange: {
        visible: false,
        status: null,
        note: "",
        saving: false,
      },
    };
  },

  computed: {
    STATUS_META() {
      return STATUS_META;
    },

    currentStatuses() {
      return CURRENT_STATUSES;
    },

    assignableStatuses() {
      return ASSIGNABLE_STATUSES;
    },

    toPickerOptions() {
      const from = this.filters.from;
      return {
        disabledDate: (date) => {
          if (from && date > addDays(new Date(from), MAX_RANGE_DAYS))
            return true;
          return false;
        },
      };
    },
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.page = 1;
        this.debouncedLoad();
      },
    },

    "filters.from"(newFrom) {
      if (!newFrom || !this.filters.to) return;
      if (
        differenceInCalendarDays(new Date(this.filters.to), new Date(newFrom)) >
        MAX_RANGE_DAYS
      ) {
        this.filters.to = format(
          addDays(new Date(newFrom), MAX_RANGE_DAYS),
          "yyyy-MM-dd"
        );
      }
    },
  },

  created() {
    this.load();
    this.loadFilters();
  },

  methods: {
    buildParams(opts = {}) {
      const params = {};

      if (this.filters.search && this.filters.search.trim().length >= 2) {
        params.search = this.filters.search.trim();
      }

      // new Date("yyyy-MM-dd") parses as UTC midnight, which in ART (UTC-3)
      // lands on the previous day and cuts today's orders out of the range.
      if (this.filters.from) {
        const [y, m, d] = this.filters.from.split("-").map(Number);
        params.from = new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
      }
      if (this.filters.to) {
        const [y, m, d] = this.filters.to.split("-").map(Number);
        params.to = new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
      }

      if (this.filters.type) params.type = this.filters.type;
      if (this.filters.doctor) params.doctor = this.filters.doctor;
      // Varias coberturas: viajan como array (coverage[0]=…), no separadas por
      // coma, porque hay nombres de cobertura que tienen comas.
      if (this.filters.coverages.length)
        params.coverage = this.filters.coverages;

      // "Todas" son los estados vigentes. Los casos viejos que quedaron en un
      // estado anterior no se muestran en la bandeja: se ven en Estadísticas.
      const statusFilters = { all: CURRENT_STATUSES };
      params.status = (
        statusFilters[this.filters.status] || [this.filters.status]
      ).join(",");

      if (!opts.forExport) {
        params.limit = this.pageSize;
        params.offset = (this.page - 1) * this.pageSize;
      }

      return params;
    },

    debouncedLoad() {
      clearTimeout(this.loadDebounce);
      this.loadDebounce = setTimeout(() => this.load(), 350);
    },

    async load() {
      // Guards against out-of-order responses: if the user changes page/filters
      // again before a previous request resolves, an older response landing
      // late must not overwrite the newer one.
      const seq = ++this.loadSeq;
      this.loading = true;
      try {
        const res = await ordersCrmService.list(this.buildParams());
        if (seq !== this.loadSeq) return;
        this.docs = res.docs || [];
        this.total = res.total || 0;
        this.counts = res.counts || this.counts;
      } catch (error) {
        if (seq !== this.loadSeq) return;
        console.error("Error loading orders CRM:", error);
        this.$message.error("Error al cargar la bandeja de órdenes");
      } finally {
        if (seq === this.loadSeq) this.loading = false;
      }
    },

    async loadFilters() {
      const isFresh = Date.now() < filtersCache.expiresAt;
      if (isFresh && filtersCache.providers && filtersCache.coverages) {
        this.providers = filtersCache.providers;
        this.coverages = filtersCache.coverages;
        return;
      }

      try {
        const [providers, coverages] = await Promise.all([
          ordersCrmService.listProviders(),
          ordersCrmService.listCoverages(),
        ]);
        this.providers = providers;
        this.coverages = coverages;
        filtersCache.providers = providers;
        filtersCache.coverages = coverages;
        filtersCache.expiresAt = Date.now() + FILTERS_CACHE_TTL_MS;
      } catch (error) {
        console.error("Error loading orders CRM filters:", error);
      }
    },

    onPageChange(newPage) {
      this.page = newPage;
      this.load();
    },

    clearFilters() {
      this.filters = {
        search: "",
        from: format(subDays(new Date(), 7), "yyyy-MM-dd"),
        to: format(new Date(), "yyyy-MM-dd"),
        type: "",
        status: "all",
        doctor: "",
        coverages: [],
      };
    },

    fullName(user) {
      return helperService.getFullName(user);
    },

    coverageOf(patient) {
      if (!patient) return { financiador: "Particular", plan: "", numero: "" };

      const financiador = getMedicalInsuranceName(patient);
      const plan = getMedicalInsurancePlan(patient);
      const numero = getMedicalInsuranceNumber(patient);

      return {
        financiador: financiador === "N/A" ? "Particular" : financiador,
        plan: plan === "N/A" ? "" : plan,
        numero: numero === "N/A" ? "" : numero,
      };
    },

    specialtyOf(doctor) {
      return doctor?.doctor?.medicalSpecialties?.[0]?.name || "";
    },

    typeMeta(row) {
      return TYPE_META[row.type] || { icon: "assignment", label: row.type };
    },

    statusOf(row) {
      return row.crm?.status || "new";
    },

    statusMeta(row) {
      const status = this.statusOf(row);
      return STATUS_META[status] || { label: statusLabel(status), cls: "" };
    },

    statusLabel,

    isTerminal(row) {
      return TERMINAL_STATUSES.includes(this.statusOf(row));
    },

    orderShortLabel(row) {
      if (row.type === "images") {
        const images = row.images?.length
          ? row.images
          : row.image
          ? [row.image]
          : [];
        if (images.length > 1) return `${images.length} estudios de imagen`;
        if (images.length === 1)
          return images[0].description || row.title || "Imagen";
        // The Indication still references an image, but that catalog entry
        // was deleted — populate silently drops it, so this isn't "no study".
        if (row.rawImagesCount > 0) return "Imagen (ya no está en el catálogo)";
        return row.title || "Imagen";
      }
      if (row.type === "lab") {
        const labs = row.labs || [];
        if (labs.length > 1) return `${labs.length} determinaciones`;
        if (labs.length === 1)
          return labs[0].description || row.title || "Laboratorio";
        if (row.rawLabsCount > 0)
          return "Laboratorio (ya no está en el catálogo)";
        return row.title || "Laboratorio";
      }
      return row.title || row.description || "Práctica";
    },

    orderItems(row) {
      if (row.type === "lab") {
        return (row.labs || []).map((lab) => ({
          key: lab._id,
          label: lab.description,
        }));
      }
      if (row.type === "images") {
        const images = row.images?.length
          ? row.images
          : row.image
          ? [row.image]
          : [];
        return images.map((image) => ({
          key: image._id,
          label: image.description,
        }));
      }
      return [];
    },

    historyOf(row) {
      return [
        { status: null, at: row.createdAt, by: null, note: null },
        ...(row.crm?.history || []),
      ];
    },

    openDetail(row) {
      this.detail.order = row;
      this.detail.observations = row.observations || "";
      this.detail.visible = true;
    },

    async saveObservations() {
      if (!this.detail.order) return;
      this.detail.savingObservations = true;
      try {
        const updated = await ordersCrmService.updateObservations(
          this.detail.order._id,
          this.detail.observations
        );
        this.detail.order = updated;
        const index = this.docs.findIndex((doc) => doc._id === updated._id);
        if (index !== -1) {
          this.docs.splice(index, 1, updated);
        }
        this.$message({ type: "success", message: "Observaciones guardadas" });
      } catch (error) {
        console.error("Error saving order observations:", error);
        this.$message.error("Error al guardar las observaciones");
      } finally {
        this.detail.savingObservations = false;
      }
    },

    async changeStatus(status, note) {
      if (!this.detail.order) return;
      try {
        const updated = await ordersCrmService.updateStatus(
          this.detail.order._id,
          status,
          note
        );
        this.detail.order = updated;
        const index = this.docs.findIndex((doc) => doc._id === updated._id);
        if (index !== -1) {
          this.docs.splice(index, 1, updated);
        }
        this.$message({ type: "success", message: "Estado actualizado" });
        this.load();
      } catch (error) {
        console.error("Error updating order status:", error);
        this.$message.error("Error al actualizar el estado");
      }
    },

    // Cada cambio de estado pasa por un diálogo con nota opcional: la nota queda
    // en el historial junto con quién hizo el cambio.
    openStatusChange(status) {
      this.statusChange.status = status;
      this.statusChange.note = "";
      this.statusChange.visible = true;
    },

    async confirmStatusChange() {
      const { status, note } = this.statusChange;
      this.statusChange.saving = true;
      try {
        await this.changeStatus(status, note);
        this.statusChange.visible = false;
        if (TERMINAL_STATUSES.includes(status)) this.detail.visible = false;
      } finally {
        this.statusChange.saving = false;
      }
    },

    statusButtonClass(status) {
      return STATUS_META[status]?.button || "button-blue-new";
    },

    async exportCsv() {
      this.exporting = true;
      try {
        const blob = await ordersCrmService.exportCsv(
          this.buildParams({ forExport: true })
        );
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `crm-ordenes-${format(new Date(), "dd-MM-yyyy")}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting orders CRM csv:", error);
        this.$message.error("Error al exportar el CSV");
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<template lang="pug">
div
  BaseSubNav(:showNavigation="false")
    template(#title)
      | CRM de órdenes

    template(#actions)
      .view-toggle
        button(
          :class="view === 'inbox' ? 'button-blue-new' : 'button-gray-new'"
          @click="view = 'inbox'"
        ) Bandeja
        button(
          :class="view === 'stats' ? 'button-blue-new' : 'button-gray-new'"
          @click="view = 'stats'"
        ) Estadísticas
      button.button-blue-new(
        v-if="showExportCsvButton && view === 'inbox'"
        :disabled="exporting"
        @click="exportCsv"
      )
        micon(name="attach_file" width="18" height="18" color="#efefef")
        span &nbsp;{{ exporting ? "Exportando..." : "Exportar CSV" }}

  .box.box--with-subnav
    .box__content--stretch(v-if="view === 'stats'")
      orders-crm-stats(:coverages="coverages")

    .box__content--stretch(v-else)
      .counters
        .counter-card
          .counter-card__icon
            micon(name="pending_actions" width="22" height="22")
          .counter-card__body
            span.counter-card__value {{ counts.active }}
            span.counter-card__label Activas
        .counter-card.is-success
          .counter-card__icon
            micon(name="event_available" width="22" height="22")
          .counter-card__body
            span.counter-card__value {{ counts.takenThisWeek }}
            span.counter-card__label Turno asignado esta semana
        .counter-card.is-muted
          .counter-card__icon
            micon(name="check_circle_outline" width="22" height="22")
          .counter-card__body
            span.counter-card__value {{ counts.closed }}
            span.counter-card__label Cerradas sin turno

      .filters-card
        .filters-card__field
          label.filters-card__label Buscar
          el-input(
            v-model="filters.search"
            placeholder="Nombre o DNI"
            prefix-icon="el-icon-search"
            clearable
          )

        .filters-card__field
          label.filters-card__label Desde
          el-date-picker(
            v-model="filters.from"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="Desde"
          )

        .filters-card__field
          label.filters-card__label Hasta
          el-date-picker(
            v-model="filters.to"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="Hasta"
            :picker-options="toPickerOptions"
          )

        .filters-card__field
          label.filters-card__label Tipo
          el-select(v-model="filters.type" placeholder="Todos")
            el-option(value="" label="Todos")
            el-option(value="lab" label="Laboratorios")
            el-option(value="images,practices" label="Prescripciones")

        .filters-card__field
          label.filters-card__label Estado
          el-select(v-model="filters.status" placeholder="Estado")
            el-option(value="all" label="Todas")
            el-option(
              v-for="key in currentStatuses"
              :key="key"
              :value="key"
              :label="STATUS_META[key].label"
            )

        .filters-card__field
          label.filters-card__label Prescriptor
          el-select(
            v-model="filters.doctor"
            placeholder="Todos"
            clearable
            filterable
          )
            el-option(
              v-for="doctor in providers"
              :key="doctor._id"
              :value="doctor._id"
              :label="fullName(doctor)"
            )

        .filters-card__field
          label.filters-card__label Cobertura
          el-select(
            v-model="filters.coverages"
            placeholder="Todas"
            multiple
            collapse-tags
            clearable
            filterable
          )
            el-option(
              v-for="coverage in coverages"
              :key="coverage.key"
              :value="coverage.key"
              :label="coverage.label"
            )

        .filters-card__field.filters-card__field--clear
          button.button-blue-new(@click="clearFilters") Limpiar filtros

      spinner(v-if="loading && !docs.length")

      .empty-state(v-if="!loading && !docs.length")
        micon(name="assignment_turned_in" width="36" height="36" color="#bdbdbd")
        p.empty-state__text No hay órdenes para los filtros aplicados

      table(v-if="docs.length" :class="{ 'is-refreshing': loading }")
        thead
          tr
            th Fecha
            th Paciente
            th Cobertura
            th Orden
            th Prescriptor
            th Estado
        tbody
          tr(
            v-for="row in docs"
            :key="row._id"
            @click="openDetail(row)"
          )
            td {{ row.createdAt | formatDateAndTime }}
            td
              strong {{ fullName(row.patient) }}
              span.table__subtext DNI {{ row.patient?.governmentId?.number || "N/A" }}
            td {{ coverageOf(row.patient).financiador }}
            td
              .table__order-type
                micon(:name="typeMeta(row).icon" width="18" height="18")
                span {{ orderShortLabel(row) }}
            td {{ row.doctor ? fullName(row.doctor) : "Dr eliminado" }}
            td
              span.status-badge(:class="statusMeta(row).cls")
                | {{ statusMeta(row).label }}

      .pagination-bar(v-if="total > pageSize")
        el-pagination.orders-crm-pagination(
          layout="prev, pager, next"
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          :disabled="loading"
          @current-change="onPageChange"
        )

  el-dialog(
    :visible.sync="detail.visible"
    title="Detalle de la orden"
    width="700px"
    custom-class="orders-crm-detail"
    center
  )
    template(v-if="detail.order")
      .detail__section
        h3.detail__heading Paciente
        p.detail__primary
          strong {{ fullName(detail.order.patient) }}
          span.detail__muted ·&nbsp;DNI {{ detail.order.patient?.governmentId?.number || "N/A" }}
        p.detail__muted {{ detail.order.patient?.email || "Sin email" }} · {{ detail.order.patient?.phone || detail.order.patient?.secondaryPhone || "Sin teléfono" }}

      .detail__divider

      .detail__section
        h3.detail__heading Cobertura
        p.detail__primary
          | {{ coverageOf(detail.order.patient).financiador }}
          template(v-if="coverageOf(detail.order.patient).plan")
            |  · {{ coverageOf(detail.order.patient).plan }}
          template(v-if="coverageOf(detail.order.patient).numero")
            |  · Nro. {{ coverageOf(detail.order.patient).numero }}

      .detail__divider

      .detail__section
        h3.detail__heading Orden
        p.detail__primary
          micon(:name="typeMeta(detail.order).icon" width="18" height="18")
          |  {{ orderShortLabel(detail.order) }}
        .chips(v-if="orderItems(detail.order).length")
          span.chip(v-for="item in orderItems(detail.order)" :key="item.key")
            | {{ item.label }}
        p.detail__muted
          | Solicitado por {{ detail.order.doctor ? fullName(detail.order.doctor) : "Dr eliminado" }}
          template(v-if="specialtyOf(detail.order.doctor)")
            |  ({{ specialtyOf(detail.order.doctor) }})

      .detail__divider

      .detail__section
        h3.detail__heading Estado
        span.status-badge(:class="statusMeta(detail.order).cls")
          | {{ statusMeta(detail.order).label }}

        p.detail__terminal-note(v-if="isTerminal(detail.order)")
          | Caso cerrado el {{ detail.order.crm.updatedAt | formatDate }}
          template(v-if="detail.order.crm.updatedBy")
            |  por {{ fullName(detail.order.crm.updatedBy) }}
          |  · Si hace falta, se puede cambiar de estado.

        //- Los botones están siempre, también en casos cerrados, para poder
        //- corregir un estado mal cargado (queda registrado en el historial).
        .detail__actions
          button.button-gray-new(
            disabled
            v-tooltip="'Próximamente'"
          )
            micon(name="call" width="16" height="16" color="#efefef")
            span &nbsp;WhatsApp
          button(
            v-for="status in assignableStatuses"
            :key="status"
            :class="statusButtonClass(status)"
            :disabled="statusOf(detail.order) === status"
            @click="openStatusChange(status)"
          ) {{ STATUS_META[status].label }}

      .detail__divider

      .detail__section
        h3.detail__heading Historial
        ul.history
          li.history__item(v-for="(entry, index) in historyOf(detail.order)" :key="index")
            span.history__date {{ entry.at | formatDateAndTime }}
            span.history__status {{ entry.status ? statusLabel(entry.status) : "Orden emitida" }}
            span.history__by(v-if="entry.by") {{ fullName(entry.by) }}
            p.history__note(v-if="entry.note")
              strong Nota:&nbsp;
              | {{ entry.note }}

      .detail__divider

      .detail__section
        h3.detail__heading Observaciones
        el-input(
          type="textarea"
          :rows="3"
          v-model="detail.observations"
          placeholder="Agregar una observación sobre esta orden..."
        )
        .detail__actions
          button.button-blue-new(
            :disabled="detail.savingObservations"
            @click="saveObservations"
          )
            | {{ detail.savingObservations ? "Guardando..." : "Guardar observaciones" }}

    template(#footer)
      button.button-gray-new(@click="detail.visible = false") Cerrar

  el-dialog(
    :visible.sync="statusChange.visible"
    :title="statusChange.status ? `Pasar a: ${statusLabel(statusChange.status)}` : ''"
    width="420px"
    append-to-body
  )
    el-input(
      type="textarea"
      v-model="statusChange.note"
      placeholder="Nota (opcional)"
      :rows="3"
    )
    template(#footer)
      .dialog-footer
        button.button-gray-new(@click="statusChange.visible = false") Cancelar
        button(
          :class="statusButtonClass(statusChange.status)"
          :disabled="statusChange.saving"
          @click="confirmStatusChange"
        )
          | {{ statusChange.saving ? "Guardando..." : "Confirmar" }}
</template>

<style lang="scss" scoped>
.box--with-subnav {
  min-height: 100px;
}

.box__content--stretch {
  padding-top: 1.25rem;
}

// ── Empty state ──────────────────────────────────────────────
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 4rem 1rem;
  width: 100%;
  background-color: $alabaster;
  border-radius: 10px;
}

.empty-state__text {
  color: $lynch;
  font-size: 1.1rem;
  margin: 0;
}

// ── Counters ─────────────────────────────────────────────────
.counters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.counter-card {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  background: $white;
  border-radius: 10px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
  padding: 0.9rem 1.4rem;
  min-width: 190px;
  flex: 1 1 190px;
}

.counter-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 50%;
  background-color: $solitude;

  .micon {
    fill: $primary;
  }
}

.counter-card.is-success .counter-card__icon {
  background-color: rgba(103, 194, 58, 0.14);

  .micon {
    fill: #67c23a;
  }
}

.counter-card.is-muted .counter-card__icon {
  background-color: $whisper;

  .micon {
    fill: $lynch;
  }
}

.counter-card__body {
  display: flex;
  flex-direction: column;
}

.counter-card__value {
  font-size: 1.6rem;
  font-weight: bold;
  color: $primary-alt;
  line-height: 1.1;
}

.counter-card__label {
  font-size: 0.8rem;
  color: $lynch;
}

// ── Filters (horizontal, above the list) ─────────────────────
.filters-card {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  background: $white;
  border-radius: 10px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
  padding: 1.1rem 1.2rem;
  margin-bottom: 1.25rem;
}

.filters-card__field {
  display: flex;
  flex-direction: column;
  flex: 1 1 160px;
  min-width: 160px;

  ::v-deep .el-input,
  ::v-deep .el-select,
  ::v-deep .el-date-editor {
    width: 100%;
  }
}

.filters-card__field--clear {
  flex: 0 0 auto;
  min-width: 0;
}

.filters-card__label {
  font-size: 0.75rem;
  font-weight: 600;
  color: $lynch;
  margin-bottom: 0.3rem;
}

// ── Table ────────────────────────────────────────────────────
table {
  table-layout: fixed;
  width: 100%;

  th:nth-child(1),
  td:nth-child(1) {
    width: 135px;
  }
  th:nth-child(2),
  td:nth-child(2) {
    width: 21%;
  }
  th:nth-child(3),
  td:nth-child(3) {
    width: 17%;
  }
  th:nth-child(4),
  td:nth-child(4) {
    width: 23%;
  }
  th:nth-child(5),
  td:nth-child(5) {
    width: 17%;
  }
  th:nth-child(6),
  td:nth-child(6) {
    width: 130px;
  }

  td strong {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.is-refreshing {
    opacity: 0.45;
    pointer-events: none;
    transition: opacity 0.15s linear;
  }
}

.table__subtext {
  display: block;
  font-size: 0.75rem;
  color: $lynch;
  margin-top: 2px;
}

.table__order-type {
  display: flex;
  align-items: center;
  gap: 0.4rem;

  .micon {
    fill: $primary;
    flex: 0 0 auto;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

tbody tr {
  cursor: pointer;
}

// ── Status badges ────────────────────────────────────────────
.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;

  &.is-new {
    background-color: #fdf6ec;
    color: #e6a23c;
  }

  &.is-callback {
    background-color: #f0f9ff;
    color: #409eff;
  }

  &.is-pending {
    background-color: #f5f0fe;
    color: #8e5fd6;
  }

  &.is-taken {
    background-color: #f0f9eb;
    color: #67c23a;
  }

  &.is-previous {
    background-color: #e8f6f5;
    color: #2a9d8f;
  }

  &.is-desisted,
  &.is-no-answer {
    background-color: #fef0f0;
    color: #f56c6c;
  }

  &.is-legacy {
    background-color: #f4f4f5;
    color: #909399;
  }
}

// ── Bandeja / Estadísticas ───────────────────────────────────
.view-toggle {
  display: flex;
  gap: 0.4rem;
  margin-right: 0.75rem;
}

// ── Pagination (Element UI) ──────────────────────────────────
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1.5rem;
}

.orders-crm-pagination {
  ::v-deep button.btn-prev,
  ::v-deep button.btn-next {
    min-width: 40px;
    height: 40px;

    i {
      font-size: 18px;
    }
  }

  ::v-deep .el-pager li {
    min-width: 40px;
    height: 40px;
    line-height: 40px;
    font-size: 15px;

    &.active {
      color: $primary;
    }

    &:hover {
      color: $primary;
    }
  }
}

// ── Detail modal ─────────────────────────────────────────────
::v-deep .el-dialog__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

::v-deep .orders-crm-detail {
  margin: 0 !important;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.detail__section {
  margin: 0.9rem 0;
}

.detail__heading {
  font-size: 0.8rem;
  font-weight: bold;
  color: $lynch;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin: 0 0 0.5rem;
}

.detail__primary {
  margin: 0.2rem 0;
  color: $primary-alt;
}

.detail__muted {
  margin: 0.2rem 0;
  color: $lynch;
  font-size: 0.9rem;
}

.detail__divider {
  height: 1px;
  margin: 1rem 0;
  background: linear-gradient(to right, transparent, $gainsboro, transparent);
}

.detail__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.85rem;
}

.detail__terminal-note {
  color: $lynch;
  margin-top: 0.5rem;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0.5rem 0;
}

.chip {
  background-color: $solitude;
  color: $lynch;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 0.75rem;
  font-weight: bold;
}

// ── History ──────────────────────────────────────────────────
.history {
  list-style: none;
  padding: 0;
  margin: 0;
}

.history__item {
  padding: 0.5rem 0;
  border-bottom: 1px solid $whisper;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.85rem;

  &:last-child {
    border-bottom: none;
  }
}

.history__date {
  color: $silver;
}

.history__status {
  font-weight: 600;
  color: $primary-alt;
}

.history__by {
  color: $lynch;
}

.history__note {
  width: 100%;
  margin: 0.4rem 0 0;
  padding: 0.5rem 0.75rem;
  background-color: $alabaster;
  border-left: 3px solid $secondary;
  border-radius: 0 4px 4px 0;
  color: $cuttysark;
  font-size: 0.85rem;
  font-style: italic;
}

// ── Dialog footers ───────────────────────────────────────────
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  width: 100%;
}
</style>
